// ABOUTME: Claude Agent SDK message types for SSE streaming.
// ABOUTME: Matches the contracts defined in docs/api/02-frontend-backend-contract.md.

// Main SDK streaming message type - union of all message types
export type SDKStreamingMessage = AssistantMessage | StreamingTextChunk | SystemMessage | ResultMessage | MessageSavedEvent;

// Assistant message containing complete content blocks
export interface AssistantMessage {
  type: 'assistant';
  content: ContentBlock[];
  model?: string | null;
  stop_reason?: string | null;
  usage?: {
    input_tokens: number;
    output_tokens: number;
  } | null;
}

// Streaming text chunk for token-by-token display (simplified format from working example)
export interface StreamingTextChunk {
  type: 'text';
  content: string;  // Just the text chunk
}

// System message for progress and status updates
export interface SystemMessage {
  type: 'system';
  subtype: 'init' | 'progress' | 'error' | 'info' | 'complete';
  message: string;
  phase?: string | null;
  progress?: number | null;
  data?: Record<string, unknown> | null;
}

// Result message indicating completion or error
export interface ResultMessage {
  type: 'result';
  subtype: 'success' | 'error_max_turns' | 'error_during_execution' | 'error_max_budget_usd';
  session_id: string;
  duration_ms: number;
  duration_api_ms: number;
  is_error: boolean;
  num_turns: number;
  total_cost_usd: number;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
  result?: string;
  errors?: string[];
}

// Message saved event - sent after assistant message is persisted to DB
// Contains the real MongoDB ID for linking pins to messages
export interface MessageSavedEvent {
  type: 'message_saved';
  message_id: string;
}

// Content blocks that appear in AssistantMessage
export type ContentBlock = TextBlock | ThinkingBlock | ToolUseBlock | ToolResultBlock | ParseErrorBlock;

// Text block for regular text content
export interface TextBlock {
  type: 'text';
  text: string;
}

// Thinking block showing agent's reasoning
export interface ThinkingBlock {
  type: 'thinking';
  thinking: string;
  signature: string;
}

// Tool use block indicating a tool call
export interface ToolUseBlock {
  type: 'tool_use';
  id: string;
  name: string;
  input: Record<string, unknown>;
}

// Tool result block containing tool execution output
export interface ToolResultBlock {
  type: 'tool_result';
  tool_use_id: string;
  content: string | Record<string, unknown>[] | null;
  is_error?: boolean;
}

// Parse error block - when structured response parsing fails
export interface ParseErrorBlock {
  type: 'parse_error';
  error_message: string;
  raw_content: string;
  details: Record<string, unknown>;
}

// Smart action (used for tool call inputs and tour actions)
export type SmartActionIcon = 'drill-down' | 'compare' | 'export' | 'filter' | 'share' | 'alert' | 'view' | 'create' | 'ask' | 'refresh';

export interface SmartAction {
  id: string;
  label: string;
  icon?: SmartActionIcon;
  action_type: 'query' | 'navigate' | 'export';
  query?: string | null;
  export_format?: 'csv' | 'pdf' | 'png' | null;
}

// Progress update data structure (commonly used in SystemMessage)
export interface ProgressData {
  phase: 'schema_discovery' | 'query_planning' | 'query_execution' | 'analysis' | 'visualization';
  message: string;
  progress: number;
}

// Helper type guards for content blocks
export function isTextBlock(block: ContentBlock): block is TextBlock {
  return block.type === 'text';
}

export function isThinkingBlock(block: ContentBlock): block is ThinkingBlock {
  return block.type === 'thinking';
}

export function isToolUseBlock(block: ContentBlock): block is ToolUseBlock {
  return block.type === 'tool_use';
}

export function isToolResultBlock(block: ContentBlock): block is ToolResultBlock {
  return block.type === 'tool_result';
}

export function isParseErrorBlock(block: ContentBlock): block is ParseErrorBlock {
  return block.type === 'parse_error';
}

// Helper type guards for message types
export function isAssistantMessage(message: SDKStreamingMessage): message is AssistantMessage {
  return message.type === 'assistant' && Array.isArray((message as any).content);
}

export function isStreamingTextChunk(message: SDKStreamingMessage): message is StreamingTextChunk {
  return message.type === 'text';
}

export function isSystemMessage(message: SDKStreamingMessage): message is SystemMessage {
  return message.type === 'system';
}

export function isResultMessage(message: SDKStreamingMessage): message is ResultMessage {
  return message.type === 'result';
}

export function isMessageSavedEvent(message: SDKStreamingMessage): message is MessageSavedEvent {
  return message.type === 'message_saved';
}

/** Data from a resolve_ambiguity tool result */
export interface ClarificationCardData {
  type: 'clarification_card';
  question: string;
  options: ClarificationOption[];
  category: 'metric' | 'dimension' | 'relationship' | 'filter' | 'time_grain' | 'analytical_intent' | 'methodology';
  persist_as: string;
  recommendation: string | null;
  workspace_id: string;
  investigation_id: string;
  additional_questions?: AdditionalQuestion[];
}

export interface AdditionalQuestion {
  question: string;
  options?: ClarificationOption[];
  category?: string;
}

export interface ClarificationOption {
  id: string;
  label: string;
  description: string;
  sql_preview?: string;
  data_preview?: string;
}

/** A single option offered by an ask_user_question question. */
export interface AskUserOption {
  label: string;
  description?: string;
}

/** One clarifying question the SIR asked via the ask_user_question tool. */
export interface AskUserQuestion {
  question: string;
  header?: string;
  options?: AskUserOption[];
  multi_select?: boolean;
  allow_free_text?: boolean;
}

/**
 * Data carried by an ask_user_question tool_use block's input.
 * The SIR emits `{ questions: [...] }`; the tool_result content carries the
 * self-contained resume payload but the interactive UI only needs the questions.
 */
export interface AskUserQuestionData {
  questions: AskUserQuestion[];
}

const CATCH_ALL_CLARIFICATION_LABELS = new Set([
  'other',
  'other please specify',
  'other specify',
  'none of the above',
  'none of these',
  'not listed',
  'n a',
  'na',
]);

function isCatchAllClarificationOption(label: string): boolean {
  const normalized = label.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  return CATCH_ALL_CLARIFICATION_LABELS.has(normalized)
    || normalized.startsWith('something else');
}

/** Parse an ask_user_question tool_use input into a typed question list. */
export function parseAskUserQuestions(input: Record<string, unknown> | undefined): AskUserQuestion[] {
  if (!input) return [];
  const raw = (input as { questions?: unknown }).questions;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((q): q is Record<string, unknown> => typeof q === 'object' && q !== null)
    .map((q) => {
      const optionsRaw = Array.isArray(q.options) ? q.options : [];
      const options: AskUserOption[] = optionsRaw
        .filter((o): o is Record<string, unknown> => typeof o === 'object' && o !== null && typeof o.label === 'string')
        .filter((o) => !isCatchAllClarificationOption(o.label as string))
        .map((o) => ({ label: o.label as string, description: typeof o.description === 'string' ? o.description : undefined }));
      return {
        question: typeof q.question === 'string' ? q.question : '',
        header: typeof q.header === 'string' ? q.header : undefined,
        options,
        multi_select: Boolean(q.multi_select),
        allow_free_text: true,
      };
    })
    .filter((q) => q.question);
}
